import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "internet-lent-instable",
  silo: "reseau",
  isPillar: true,
  h1: "Internet lent ou instable : comment améliorer ma connexion ?",
  metaTitle: "Internet lent : causes et solutions pour une connexion rapide",
  metaDescription: "Votre connexion internet est lente ou coupe souvent ? Box, WiFi, DNS... Trouvez la cause et accélérez votre connexion dès aujourd'hui.",
  excerpt: "Vidéos qui bufferisent, pages qui mettent une éternité à charger, appels visio qui sautent... Une connexion internet lente ou instable gâche le quotidien. Voici comment identifier la cause et y remédier.",
  publishedAt: "2024-01-15",
  readingMinutes: 6,
  relatedSlugs: ["wifi-ne-fonctionne-plus", "pc-lent-windows-10-11", "applications-web-m365-google-workspace"],
  faq: [
    {
      question: "Comment mesurer la vitesse de ma connexion internet ?",
      answer: "Rendez-vous sur speedtest.net ou fast.com et lancez le test. Comparez le résultat avec le débit théorique de votre abonnement. Si vous obtenez moins de 50% du débit annoncé, il y a un problème à investiguer.",
    },
    {
      question: "Ma connexion est lente seulement le soir -- pourquoi ?",
      answer: "C'est souvent dû à une saturation du réseau de votre opérateur aux heures de pointe (17h-22h). Si le problème est systématique, signalez-le à votre opérateur. Ce n'est pas un problème que vous pouvez résoudre seul.",
    },
    {
      question: "Changer les DNS peut-il vraiment accélérer ma connexion ?",
      answer: "Oui, pour le chargement des pages web. Les DNS traduisent les noms de sites en adresses IP. Les DNS de votre opérateur sont parfois lents. Passer aux DNS de Google (8.8.8.8) ou Cloudflare (1.1.1.1) peut accélérer l'affichage des pages de 10 à 30%.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Avant de blâmer votre opérateur, sachez que la lenteur d'internet vient souvent d'un problème chez vous : position de la box, câbles usés, WiFi saturé, ou paramètres réseau sous-optimaux. Voici comment diagnostiquer étape par étape.",
    },
    {
      type: "h2",
      text: "Étape 1 : tester votre débit réel",
    },
    {
      type: "p",
      text: "Commencez par mesurer votre débit réel sur speedtest.net. Faites le test deux fois : une fois en WiFi et une fois en câble Ethernet branché directement sur la box. Si le câble est nettement plus rapide, le problème vient du WiFi. Si les deux sont lents, le problème vient de votre ligne ou de votre box.",
    },
    {
      type: "h2",
      text: "Causes courantes d'une connexion lente",
    },
    {
      type: "h3",
      text: "Votre box est mal placée",
    },
    {
      type: "p",
      text: "La box doit être posée en hauteur, dans un endroit dégagé, loin des murs épais, des fours à micro-ondes et des téléphones sans fil qui perturbent le signal WiFi.",
    },
    {
      type: "h3",
      text: "Le [WiFi est saturé ou perturbé](slug:wifi-ne-fonctionne-plus)",
    },
    {
      type: "p",
      text: "Dans un immeuble, les réseaux WiFi des voisins peuvent interférer avec le vôtre. Changer de canal WiFi dans les paramètres de votre box peut améliorer significativement la situation.",
    },
    {
      type: "h3",
      text: "Câble Ethernet défectueux ou câble téléphonique vieillissant",
    },
    {
      type: "p",
      text: "Les vieux câbles téléphoniques (RJ11) peuvent dégrader la connexion ADSL. Un câble Ethernet plié ou endommagé peut aussi réduire les performances. Essayez de remplacer les câbles si leur état est douteux.",
    },
    {
      type: "h3",
      text: "Box ou modem qui a besoin d'être redémarré",
    },
    {
      type: "p",
      text: "Comme un ordinateur, votre box accumule des erreurs avec le temps. Un redémarrage simple (débranchez l'alimentation 30 secondes, rebranchez) résout souvent des problèmes de lenteur inexpliqués.",
    },
    {
      type: "h3",
      text: "Programmes qui consomment la bande passante en arrière-plan",
    },
    {
      type: "p",
      text: "Windows Update, une sauvegarde cloud, ou un antivirus qui télécharge des mises à jour peuvent consommer toute votre bande passante sans que vous le sachiez. Vérifiez dans le Gestionnaire des tâches, onglet 'Réseau'.",
    },
    {
      type: "callout",
      variant: "tip",
      title: "Changer ses DNS en 2 minutes",
      text: "Panneau de configuration > Centre réseau > votre connexion > Propriétés > Protocole Internet version 4 (TCP/IPv4). Saisissez 8.8.8.8 (serveur DNS préféré) et 8.8.4.4 (autre serveur DNS). Validez et redémarrez votre navigateur.",
    },
    {
      type: "h2",
      text: "Quand le problème vient de votre opérateur",
    },
    {
      type: "p",
      text: "Si votre débit est lent même en câble, loin de la box, et que le redémarrage de la box ne change rien, le problème vient probablement de votre ligne ou de votre opérateur. Contactez le service technique de votre fournisseur et demandez un diagnostic de ligne. Nos techniciens peuvent vous accompagner dans ces démarches.",
    },
  ],
};

export default article;
